/**
 * SearchUsersSheet
 * Wrapper for showing search users sheet using SheetService
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart
 *
 * Two modes:
 *   - 'message' (default): existing share flow (selection + message + send).
 *   - 'mention': picker returning a single user; mirrors Flutter's
 *     BottomSheetResultType.mention return path used by comments_bottom_sheet.
 *
 * Both entry points stand the sheet shell down as a scroller (`scrollable: false`)
 * because the body owns its own nested scroll region (the users list), with the
 * search field, the selected-users strip and the message composer sitting outside
 * it. That hands wheel events to the list and lets the drag-vs-scroll decision be
 * made from the list's own scroll position — mirroring Flutter, where the sheet
 * drag is gated on the list controller's `_scrolledToTop`
 * (search_users_bottom_sheet.dart:447-509, controller wired to the CustomScrollView
 * at :510-520) and the composer lives outside the scroll view (:525). Same
 * configuration as the sibling ContentCommentsSheet / ContentGiftsSheet /
 * ContentUsersLikedSheet.
 */

import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { SearchUsersContent } from './components/SearchUsersContent';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

const show = (
  shareInfo: ShareInfoEntity,
  container?: HTMLElement | string | null,
  screenKey?: string,
  width?: string,
) => {
  SheetService.show({
    title: '',
    content: <SearchUsersContent shareInfo={shareInfo} mode="message" />,
    container,
    screenKey,
    fixedHeight: '85dvh',
    scrollable: false,
    width: width,
    qaId: 'search-users',
  });
};

/**
 * Open the sheet in mention-picker mode. Resolves with the selected user's
 * username (without '@'), or null if dismissed without selecting.
 */
const showForMention = (
  container?: HTMLElement | string | null,
  screenKey?: string,
  width?: string,
): Promise<string | null> => {
  return new Promise<string | null>((resolve) => {
    let resolved = false;
    
    const resolveOnce = (username: string | null) => {
      if (resolved) return;
      resolved = true;
      resolve(username);
    };

    SheetService.show({
      title: '',
      content: (
        <SearchUsersContent
          mode="mention"
          onSelectUser={(sub: ExclusiveContentSubscriptionEntity) => {
            resolveOnce(sub.username);
            SheetService.close();
          }}
        />
      ),
      container,
      screenKey,
      fixedHeight: '85dvh',
      scrollable: false,
      width,
      // Distinct from 'search-users': the mention picker is opened stacked on top
      // of the comments sheet, and every sheet in the stack is rendered at once.
      qaId: 'search-users-mention',
      onClose: () => resolveOnce(null),
    });
  });
};

export const SearchUsersSheet = {
  show,
  showForMention,
};
